import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ConfigProvider, Form, Input, Button, Select, Card, message, Row, Col, Table, Space, Popconfirm, Modal, Layout, Avatar, Dropdown, Collapse, Empty, Badge, Tag, Tabs, Popover } from 'antd';
import { UserOutlined, LockOutlined, EditOutlined, DeleteOutlined, PlusOutlined, LogoutOutlined, RightOutlined, FileTextOutlined, CheckCircleOutlined, CloseCircleOutlined, AuditOutlined, TableOutlined, ExclamationCircleOutlined, EyeOutlined } from '@ant-design/icons';
import zhCN from 'antd/locale/zh_CN';
import MAPolicyForm from './components/MAPolicyForm';
import ApprovalForm from './components/ApprovalForm';
import DataTable from './components/DataTable';
import { AuthProvider, useAuth } from './contexts/AuthContext';

const { Option } = Select;
const { Header, Content } = Layout;
const { Panel } = Collapse;

// Login Component
const Login = () => {
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const [isChangePasswordVisible, setIsChangePasswordVisible] = useState(false);
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const { login, user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (user) {
      navigate('/mars');
    }
  }, [user, navigate]);

  // 如果正在检查认证状态，显示加载中
  if (authLoading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Card style={{ textAlign: 'center' }}>
          <div>正在检查登录状态...</div>
        </Card>
      </div>
    );
  }

  const roleOptions = [
    { value: 'super_admin', label: '超级管理员' },
    { value: 'admin', label: 'RMA' },
    { value: 'editor', label: 'MA' },
    { value: 'visitor', label: '分区负责人' }
  ];

  const regionOptions = [
    { value: 'north', label: '北区' },
    { value: 'east', label: '东区' },
    { value: 'south', label: '南区' },
    { value: 'center', label: '中区' }
  ];

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const result = await login(values);
      
      // 检查是否需要修改密码（密码为默认密码123456）
      if (result.requirePasswordChange) {
        setLoading(false);
        Modal.warning({
          title: '密码安全提醒',
          content: '您的密码是默认密码，为您跳转到修改密码页面，请修改密码后重新登录',
          okText: '前往修改密码',
          onOk() {
            // 打开修改密码弹窗，并自动填充用户信息
            setIsChangePasswordVisible(true);
            passwordForm.setFieldsValue({
              username: result.userInfo.username,
              role: result.userInfo.role,
              region: result.userInfo.region
            });
          },
        });
        return; // 不继续登录流程
      }
      
      if (result.success) {
        message.success('登录成功');
        
        // 检查是否有待审批记录（仅对管理员和特定访问者）
        const loggedInUser = result.user;
        const canApprove = ['admin', 'super_admin'].includes(loggedInUser.role) || 
                          (loggedInUser.role === 'visitor' && loggedInUser.delegate_editor_id);
        
        if (canApprove) {
          try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/approval/pending-count', {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            
            if (response.ok) {
              const data = await response.json();
              if (data.count > 0) {
                // 显示待审批提示弹窗
                Modal.info({
                  title: '待审批提醒',
                  icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
                  content: (
                    <div>
                      <p>您有 <strong style={{ color: '#faad14', fontSize: '16px' }}>{data.count}</strong> 条待审批记录</p>
                      <p style={{ color: '#666', fontSize: '14px' }}>请在数据列表中点击待审批记录进行审批操作</p>
                    </div>
                  ),
                  okText: '知道了',
                  onOk() {
                    navigate('/mars');
                  },
                });
                return; // 不立即跳转,等待用户关闭弹窗
              }
            }
          } catch (error) {
            console.error('检查待审批记录失败:', error);
          }
        }
        
        // 如果没有待审批记录或检查失败，直接跳转到mars
        navigate('/mars');
      } else {
        message.error(result.message);
      }
    } catch (error) {
      console.error('登录失败:', error);
      message.error('登录失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = (value) => {
    setSelectedRole(value);
    form.setFieldsValue({ region: undefined });
  };

  const getRegionOptions = () => {
    if (selectedRole === 'visitor' || selectedRole === 'super_admin') {
      return [];
    }
    return regionOptions;
  };

  const handleChangePassword = async (values) => {
    setChangePasswordLoading(true);
    try {
      const response = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(values),
      });

      const data = await response.json();

      if (response.ok) {
        message.success(data.message);
        setIsChangePasswordVisible(false);
        passwordForm.resetFields();
      } else {
        message.error(data.message || '密码修改失败');
      }
    } catch (error) {
      console.error('修改密码失败:', error);
      message.error('修改密码失败，请重试');
    } finally {
      setChangePasswordLoading(false);
    }
  };

  return (
    <div style={{ 
      minHeight: 'calc(100vh - 60px)', 
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <Row justify="center" style={{ width: '100%' }}>
        <Col xs={22} sm={16} md={12} lg={8} xl={6}>
          <Card 
            title="MA权限管理平台" 
            style={{ 
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              borderRadius: '8px'
            }}
          >
            <Form
              form={form}
              name="login"
              onFinish={onFinish}
              layout="vertical"
              size="large"
            >
              <Form.Item
                name="role"
                label="角色"
                rules={[{ required: true, message: '请选择角色' }]}
              >
                <Select 
                  placeholder="请选择角色"
                  onChange={handleRoleChange}
                >
                  {roleOptions.map(option => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              {selectedRole && selectedRole !== 'super_admin' && selectedRole !== 'visitor' && (
                <Form.Item
                  name="region"
                  label="区域"
                  rules={[{ required: true, message: '请选择区域' }]}
                >
                  <Select placeholder="请选择区域">
                    {getRegionOptions().map(option => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              )}

              <Form.Item
                name="username"
                label="用户名"
                rules={[{ required: true, message: '请输入用户名' }]}
              >
                <Input 
                  prefix={<UserOutlined />} 
                  placeholder="请输入用户名" 
                />
              </Form.Item>

              <Form.Item
                name="password"
                label="密码"
                rules={[{ required: true, message: '请输入密码' }]}
              >
                <Input.Password 
                  prefix={<LockOutlined />} 
                  placeholder="请输入密码" 
                />
              </Form.Item>

              <Form.Item>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={loading}
                  style={{ width: '100%', marginBottom: '12px' }}
                >
                  登录
                </Button>
                <Button 
                  type="link" 
                  onClick={() => setIsChangePasswordVisible(true)}
                  style={{ width: '100%', padding: 0 }}
                >
                  修改密码
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>

      {/* 修改密码弹窗 */}
      <Modal
        title="修改密码"
        open={isChangePasswordVisible}
        onCancel={() => {
          setIsChangePasswordVisible(false);
          passwordForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handleChangePassword}
        >
          <Form.Item
            name="role"
            label="角色"
            rules={[{ required: true, message: '请选择角色' }]}
          >
            <Select placeholder="请选择角色">
              {roleOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.role !== currentValues.role}
          >
            {({ getFieldValue }) => {
              const role = getFieldValue('role');
              if (role && role !== 'super_admin' && role !== 'visitor') {
                return (
                  <Form.Item
                    name="region"
                    label="区域"
                    rules={[{ required: true, message: '请选择区域' }]}
                  >
                    <Select placeholder="请选择区域">
                      {regionOptions.map(option => (
                        <Option key={option.value} value={option.value}>
                          {option.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                );
              }
              return null;
            }}
          </Form.Item>

          <Form.Item
            name="username"
            label="用户名"
            rules={[{ required: true, message: '请输入用户名' }]}
          >
            <Input prefix={<UserOutlined />} placeholder="请输入用户名" />
          </Form.Item>

          <Form.Item
            name="oldPassword"
            label="旧密码"
            rules={[{ required: true, message: '请输入旧密码' }]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="请输入旧密码" />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              {
                pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                message: '密码必须至少8位，且包含大小写字母、数字和特殊字符(@$!%*?&)'
              }
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="请输入新密码" />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="确认新密码"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: '请确认新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="请再次输入新密码" />
          </Form.Item>

          <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f0f2f5', borderRadius: '4px' }}>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>密码要求：</div>
            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '12px', color: '#666' }}>
              <li>至少8个字符</li>
              <li>包含大写字母（A-Z）</li>
              <li>包含小写字母（a-z）</li>
              <li>包含数字（0-9）</li>
              <li>包含特殊字符（@$!%*?&）</li>
            </ul>
          </div>

          <Form.Item style={{ marginBottom: 0 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => {
                setIsChangePasswordVisible(false);
                passwordForm.resetFields();
              }}>
                取消
              </Button>
              <Button type="primary" htmlType="submit" loading={changePasswordLoading}>
                确认修改
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

// Dashboard Component
const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // 先检查用户是否存在
  React.useEffect(() => {
    if (!user) {
      // 如果没有用户信息，重定向到登录页
      navigate('/login', { replace: true });
    }
  }, [user, navigate]);

  if (!user) {
    // 在重定向之前显示加载状态
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <Card style={{ textAlign: 'center' }}>
          <div>请先登录...</div>
        </Card>
      </div>
    );
  }

  // 所有角色都使用分组显示页面
  return <GroupedDashboard user={user} logout={logout} />;
};

// Grouped Dashboard Component - 按分区和省份分组显示（访问者只读，超级管理员可编辑）
const GroupedDashboard = ({ user, logout }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isApprovalModalVisible, setIsApprovalModalVisible] = useState(false);
  const [approvalRecord, setApprovalRecord] = useState(null);
  const [approvalAction, setApprovalAction] = useState('create');
  const [oldRecordData, setOldRecordData] = useState(null);
  const [isCityMode, setIsCityMode] = useState(false); // 是否为城市表模式
  const navigate = useNavigate();

  const isReadOnly = user.role === 'visitor' && !user.delegate_editor_id;
  const isAdmin = user.role === 'admin';
  const isSuperAdmin = user.role === 'super_admin';
  const isEditor = user.role === 'editor' || user.role === 'city_editor';
  const isDelegateVisitor = user.role === 'visitor' && user.delegate_editor_id;
  const canEdit = isAdmin || isSuperAdmin || isEditor || isDelegateVisitor;
  
  // 检查delegate visitor是否可以审批/删除指定记录
  const canDelegateVisitorManage = (record) => {
    if (!isDelegateVisitor) return false;
    // 可以管理created_by匹配或ma_name='李金雪'的记录
    return record.created_by === user.delegate_editor_id || record.ma_name === '李金雪';
  };
  
  // 分区-省份-城市映射数据
  const divisionProvinceMap = {
    '南区': ['广东省', '福建省', '广西', '贵州省', '海南省', '湖南省', '江西省', '四川省', '云南省', '重庆'],
    '北区': ['河北省', '北京', '黑龙江省', '吉林省', '辽宁省', '内蒙', '山西省', '天津'],
    '中区': ['山东省', '甘肃省', '河南省', '湖北省', '宁夏', '青海省', '陕西省', '新疆'],
    '东区': ['安徽省', '江苏省', '上海', '浙江省']
  };

  const provinceCityMap = {
    '山东省': ['济南', '青岛', '烟台','威海','日照','潍坊','淄博','东营','滨州'],
    '陕西省': ['西安', '安康','宝鸡','铜川','渭南','榆林'],
    '新疆': ['乌鲁木齐', '阿克苏', '喀什','昌吉','克拉玛依','伊犁'],
    '甘肃省': ['兰州', '天水','酒泉','庆阳','武威','白银','临夏'],
    '河南省': ['平顶山','南阳','濮阳','安阳','开封','信阳','周口','郑州','洛阳','新乡'],
    '湖北省': ['襄阳','十堰','宜昌','荆州','武汉','黄石','黄冈','恩施'],
    '青海省': ['西宁'],
    '宁夏': ['银川'],
    '浙江省': ['杭州', '绍兴', '宁波', '湖州', '嘉兴', '舟山', '台州', '温州', '金华', '丽水', '衢州'],
    '江苏省': ['南京', '苏州', '无锡', '常州', '镇江', '连云港', '淮安', '盐城', '宿迁', '徐州', '泰州', '扬州', '南通'],
    '安徽省': ['合肥', '淮南', '芜湖', '蚌埠', '安庆', '阜阳', '滁州', '宿州', '马鞍山'],
    '上海': [],
    '河北省': ['石家庄', '保定', '邯郸', '邢台'],
    '黑龙江省': ['哈尔滨', '大庆', '齐齐哈尔', '牡丹江'],
    '内蒙': ['呼和浩特', '包头', '赤峰', '鄂尔多斯'],
    '山西省': ['太原', '大同', '运城', '晋城'],
    '辽宁省': ['沈阳', '大连', '鞍山', '营口'],
    '吉林省': ['长春', '吉林市', '延边州', '松原'],
    '北京': ['海淀', '朝阳', '东城', '西城', '丰台'],
    '广东省': ['广东省医保', '广东省市场管理局', '广东省工信厅', '广东省卫健', '广东省药监局', '潮州市', '汕头市', '汕尾市', '河源市', '清远市', '广州市', '广州市医保', '广州市卫健', '佛山市', '珠海市', '江门市', '阳江市', '东莞市', '肇庆市', '云浮市', '惠州市', '梅州市', '揭阳市', '茂名市', '深圳市', '湛江市', '韶关市', '中山市'],
    '海南省': ['儋州市', '海口市', '三亚市', '省直辖县级行政区划'],
    '广西': ['百色市', '北海市', '防城港市', '贵港市', '桂林市', '河池市', '贺州市', '来宾市', '柳州市', '南宁市', '钦州市', '梧州市', '玉林市'],
    '湖南省': ['常德市', '郴州市', '衡阳市', '怀化市', '娄底市', '邵阳市', '湘潭市', '益阳市', '永州市', '岳阳市', '长沙市', '株洲市'],
    '江西省': ['抚州市', '赣州市', '吉安市', '景德镇市', '九江市', '南昌市', '萍乡市', '上饶市', '新余市', '宜春市', '鹰潭市'],
    '福建省': ['福州市', '龙岩市', '南平市', '宁德市', '莆田市', '泉州市', '三明市', '厦门市', '漳州市'],
    '四川省': ['巴中市', '成都市', '达州市', '德阳市', '广安市', '广元市', '乐山市', '凉山彝族自治州', '泸州市', '眉山市', '绵阳市', '南充市', '内江市', '攀枝花市', '遂宁市', '雅安市', '宜宾市', '资阳市', '自贡市'],
    '贵州省': ['黔东南苗族侗族自治州', '黔南布依族苗族自治州', '黔西南布依族苗族自治州', '遵义市','安顺市', '毕节市', '贵阳市', '六盘水市'],
    '重庆': ['重庆市'],
    '云南省': ['保山市', '楚雄彝族自治州', '红河哈尼族彝族自治州', '昆明市', '普洱市', '曲靖市', '文山壮族苗族自治州', '西双版纳傣族自治州', '玉溪市', '昭通市', '大理白族自治州']
  };

  // 计算完成率统计
  const calculateCompletionStats = (division, dataList) => {
    const provinces = divisionProvinceMap[division] || [];
    const totalProvinces = provinces.length;
    
    // 计算该分区所有主责城市总数
    let totalCities = 0;
    provinces.forEach(province => {
      const cities = provinceCityMap[province] || [];
      totalCities += cities.length;
    });
    
    // 统计已填写的省份（去重）
    const filledProvinces = new Set();
    dataList.forEach(record => {
      if (record.division === division && record.province && !record.responsible_city) {
        filledProvinces.add(record.province);
      }
    });
    
    // 统计已填写的主责城市（去重）
    const filledCities = new Set();
    dataList.forEach(record => {
      if (record.division === division && record.responsible_city) {
        filledCities.add(`${record.province}-${record.responsible_city}`);
      }
    });
    
    // 未填写的省份
    const unfilledProvinces = provinces.filter(p => !filledProvinces.has(p));
    
    // 未填写的主责城市
    const unfilledCities = [];
    provinces.forEach(province => {
      const cities = provinceCityMap[province] || [];
      cities.forEach(city => {
        if (!filledCities.has(`${province}-${city}`)) {
          unfilledCities.push(`${province}-${city}`);
        }
      });
    });
    
    return {
      provinceCompletion: {
        filled: filledProvinces.size,
        total: totalProvinces,
        rate: totalProvinces > 0 ? (filledProvinces.size / totalProvinces * 100).toFixed(1) : '0.0',
        filledList: Array.from(filledProvinces),
        unfilledList: unfilledProvinces
      },
      cityCompletion: {
        filled: filledCities.size,
        total: totalCities,
        rate: totalCities > 0 ? (filledCities.size / totalCities * 100).toFixed(1) : '0.0',
        filledList: Array.from(filledCities),
        unfilledList: unfilledCities
      }
    };
  };
  
  // 判断用户是否同时拥有editor角色和can_create_city权限
  const hasBothCapabilities = user.role === 'editor' && (user.can_create_city === true || user.can_create_city === 1 || user.can_create_city === '1');
  // 判断用户是否只能创建城市表（纯city_editor）
  const isOnlyCityEditor = user.role === 'city_editor';

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/ma-policy', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        setData(result);
      } else if (response.status === 401 || response.status === 403) {
        // Token失效或无权限，清除本地token并跳转到登录页
        message.error('登录已过期，请重新登录');
        logout();
        navigate('/login', { replace: true });
      } else {
        message.error('获取数据失败');
      }
    } catch (error) {
      console.error('获取数据失败:', error);
      message.error('获取数据失败');
    } finally {
      setLoading(false);
    }
    
    // 返回 Promise 以便调用者可以等待
    return Promise.resolve();
  };

  React.useEffect(() => {
    fetchData();
  }, []);

  const handleLogout = () => {
    logout();
    message.success('已退出登录');
    navigate('/login');
  };

  const handleViewDetail = (record) => {
    setViewingRecord(record);
    // 根据记录是否有responsible_city来设置城市模式
    setIsCityMode(!!record.responsible_city);
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    setViewingRecord(record);
    // 根据记录是否有responsible_city来设置城市模式
    setIsCityMode(!!record.responsible_city);
    setIsModalVisible(true);
  };

  const handleDelete = async (id) => {
    // 保存当前滚动位置
    const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/ma-policy/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        message.success('删除成功');
        await fetchData();
        
        // 恢复滚动位置
        setTimeout(() => {
          window.scrollTo({
            top: scrollPosition,
            behavior: 'instant'
          });
        }, 0);
      } else if (response.status === 401 || response.status === 403) {
        // Token失效或无权限，清除本地token并跳转到登录页
        message.error('登录已过期，请重新登录');
        logout();
        navigate('/login', { replace: true });
      } else {
        const error = await response.json();
        message.error('删除失败：' + (error.message || '未知错误'));
      }
    } catch (error) {
      console.error('删除失败:', error);
      message.error('删除失败');
    }
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setViewingRecord(null);
  };

  const handleFormSuccess = async () => {
    // 保存当前滚动位置
    const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
    
    setIsModalVisible(false);
    setViewingRecord(null);
    
    await fetchData();
    
    // 恢复滚动位置
    setTimeout(() => {
      window.scrollTo({
        top: scrollPosition,
        behavior: 'instant'
      });
    }, 0);
  };

  // 审批相关处理函数
  const handleApproval = async (record) => {
    // 获取该数据的详细信息（包含旧数据用于对比）
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/approval/${record.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        setApprovalRecord(result.data);
        
        // 判断操作类型：
        // 关键修复：只要有oldData（pending_changes），就是update
        // 不应该根据审批历史来判断
        const isUpdate = !!result.oldData;
        setApprovalAction(isUpdate ? 'update' : 'create');
        
        // 设置旧数据（用于对比变更）
        setOldRecordData(result.oldData || null);
        
        console.log('审批数据加载:', {
          hasApprovalHistory: result.approvalHistory && result.approvalHistory.length > 0,
          hasOldData: !!result.oldData,
          action: isUpdate ? 'update' : 'create',
          recordId: record.id,
          oldDataFields: result.oldData ? Object.keys(result.oldData) : []
        });
        
        setIsApprovalModalVisible(true);
      } else if (response.status === 401 || response.status === 403) {
        // Token失效或无权限，清除本地token并跳转到登录页
        message.error('登录已过期，请重新登录');
        logout();
        navigate('/login', { replace: true });
      } else {
        message.error('获取审批数据失败');
      }
    } catch (error) {
      console.error('获取审批数据失败:', error);
      message.error('获取审批数据失败');
    }
  };

  const handleApprovalSuccess = async () => {
    // 保存当前滚动位置
    const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
    
    setIsApprovalModalVisible(false);
    setApprovalRecord(null);
    
    // 等待数据刷新完成
    await fetchData();
    
    // 恢复滚动位置
    // 使用 setTimeout 确保 DOM 已更新
    setTimeout(() => {
      window.scrollTo({
        top: scrollPosition,
        behavior: 'instant' // 立即跳转，不使用平滑滚动
      });
    }, 0);
    
    // message.success('审批完成，已发送邮件通知MA');
  };

  // 获取审批状态标签
  const getApprovalStatusTag = (status) => {
    switch (status) {
      case 'pending':
        return <Tag color="orange" style={{ marginLeft: '8px' }}>待审批</Tag>;
      case 'approved':
        return <Tag color="green" style={{ marginLeft: '8px' }}>已通过</Tag>;
      case 'rejected':
        return <Tag color="red" style={{ marginLeft: '8px' }}>未通过</Tag>;
      case 'partial':
        return <Tag color="blue" style={{ marginLeft: '8px' }}>部分通过</Tag>;
      default:
        return null;
    }
  };

  // 分区显示顺序
  const divisionOrder = ['北区', '东区', '南区', '中区'];

  // 根据用户名确定优先展示的区域
  const getPriorityDivision = () => {
    if (user.role === 'visitor') {
      const visitorPriorityMap = {
        '阴旗俊': '北区',
        '杨志涛': '东区',
        '何凤东': '中区',
        '曾志红': '南区'
      };
      return visitorPriorityMap[user.name] || null;
    }
    return null;
  };

  // 获取排序后的分区顺序（优先区域置顶）
  const getSortedDivisionOrder = () => {
    const priorityDivision = getPriorityDivision();
    if (priorityDivision) {
      // 将优先区域移到最前面
      return [priorityDivision, ...divisionOrder.filter(d => d !== priorityDivision)];
    }
    return divisionOrder;
  };

  // 获取默认展开的折叠框key
  const getDefaultActiveKeys = () => {
    const priorityDivision = getPriorityDivision();
    if (priorityDivision) {
      // 访问者：只展开优先区域
      return [priorityDivision];
    }
    // 其他角色：展开所有区域
    return divisionOrder;
  };

  // 按分区和省份分组数据
  const groupDataByDivisionAndProvince = () => {
    const grouped = {};
    
    data.forEach(item => {
      const division = item.division || '未知分区';
      const province = item.province || '未知省份';
      
      if (!grouped[division]) {
        grouped[division] = {};
      }
      if (!grouped[division][province]) {
        grouped[division][province] = [];
      }
      grouped[division][province].push(item);
    });

    return grouped;
  };

  const groupedData = groupDataByDivisionAndProvince();

  // 用户下拉菜单
  const userMenuItems = [
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: handleLogout
    }
  ];

  return (
    <Layout style={{ minHeight: 'auto' }}>
      <Header style={{ 
        background: '#fff', 
        padding: '0 24px', 
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <h2 style={{ margin: 0, color: '#1890ff', fontSize: '20px' }}>
            MA权限管理平台
          </h2>
          <span style={{ 
            marginLeft: '24px', 
            fontSize: '16px', 
            color: '#666' 
          }}>
            欢迎 <strong style={{ color: '#1890ff' }}>{user.name}</strong>
          </span>
        </div>
        
        <Dropdown
          menu={{ items: userMenuItems }}
          placement="bottomRight"
          trigger={['click']}
        >
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            cursor: 'pointer',
            padding: '8px 12px',
            borderRadius: '6px',
            transition: 'background-color 0.3s'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            <Avatar 
              size="small" 
              icon={<UserOutlined />} 
              style={{ backgroundColor: '#52c41a', marginRight: '8px' }}
            />
            <span style={{ color: '#666' }}>{user.name}</span>
          </div>
        </Dropdown>
      </Header>

      <Content style={{ padding: '16px 24px', background: '#f0f2f5' }}>
        {loading ? (
          <Card loading={true}>加载中...</Card>
        ) : data.length === 0 ? (
          <Card>
            {canEdit && (
              <div style={{ marginBottom: '16px', textAlign: 'right', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                {/* 管理员、同时拥有editor和city_editor能力的用户，显示两个按钮 */}
                {isAdmin || hasBothCapabilities ? (
                  <>
                    <Button 
                      type="primary" 
                      icon={<PlusOutlined />}
                      onClick={() => {
                        setViewingRecord(null);
                        setIsCityMode(false);
                        setIsModalVisible(true);
                      }}
                    >
                      新增省级政策情况
                    </Button>
                    <Button 
                      type="primary" 
                      icon={<PlusOutlined />}
                      onClick={() => {
                        setViewingRecord(null);
                        setIsCityMode(true);
                        setIsModalVisible(true);
                      }}
                    >
                      新增主责城市政策情况
                    </Button>
                  </>
                ) : isOnlyCityEditor ? (
                  <Button 
                    type="primary" 
                    icon={<PlusOutlined />}
                    onClick={() => {
                      setViewingRecord(null);
                      setIsCityMode(true);
                      setIsModalVisible(true);
                    }}
                  >
                    新增主责城市政策情况
                  </Button>
                ) : (
                  <Button 
                    type="primary" 
                    icon={<PlusOutlined />}
                    onClick={() => {
                      setViewingRecord(null);
                      setIsCityMode(false);
                      setIsModalVisible(true);
                    }}
                  >
                    新增省级政策情况
                  </Button>
                )}
              </div>
            )}
            <Empty description="暂无数据" />
          </Card>
        ) : (isEditor || isAdmin) ? (
          // 编辑者和管理员视图：简单列表 + 新增按钮
          <div>
            {/* 按类型分类显示卡片 */}
            {/* 省级政策情况 */}
            {data.filter(r => !r.responsible_city).length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '16px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{
                      width: '4px',
                      height: '22px',
                      backgroundColor: '#1890ff',
                      borderRadius: '2px',
                      marginRight: '12px'
                    }} />
                    <span style={{ 
                      fontSize: '18px', 
                      fontWeight: '600', 
                      color: '#1890ff'
                    }}>
                      省级政策情况
                    </span>
                    <span style={{
                      marginLeft: '12px',
                      fontSize: '13px',
                      color: '#999'
                    }}>
                      共 {data.filter(r => !r.responsible_city).length} 条
                    </span>
                    {/* 完成率统计 */}
                    {isAdmin && user.region && (
                      <Popover
                        content={
                          <div style={{ maxWidth: '300px' }}>
                            {(() => {
                              const stats = calculateCompletionStats(user.region === 'north' ? '北区' : user.region === 'east' ? '东区' : user.region === 'south' ? '南区' : '中区', data);
                              return (
                                <div>
                                  <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>已填写省份 ({stats.provinceCompletion.filled}):</div>
                                  <div style={{ marginBottom: '12px', color: '#52c41a' }}>
                                    {stats.provinceCompletion.filledList.length > 0 ? stats.provinceCompletion.filledList.join('、') : '无'}
                                  </div>
                                  <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>未填写省份 ({stats.provinceCompletion.unfilledList.length}):</div>
                                  <div style={{ color: '#ff4d4f' }}>
                                    {stats.provinceCompletion.unfilledList.length > 0 ? stats.provinceCompletion.unfilledList.join('、') : '无'}
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        }
                        title="省级完成情况"
                        trigger="hover"
                      >
                        <span style={{
                          marginLeft: '16px',
                          padding: '2px 8px',
                          backgroundColor: '#e6f7ff',
                          color: '#1890ff',
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}>
                          {(() => {
                            const stats = calculateCompletionStats(user.region === 'north' ? '北区' : user.region === 'east' ? '东区' : user.region === 'south' ? '南区' : '中区', data);
                            return `完成率: ${stats.provinceCompletion.filled}/${stats.provinceCompletion.total} (${stats.provinceCompletion.rate}%)`;
                          })()}
                        </span>
                      </Popover>
                    )}
                  </div>
                  
                  {/* 新增按钮区域 */}
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {/* 管理员、同时拥有editor和city_editor能力的用户，显示两个按钮 */}
                    {isAdmin || hasBothCapabilities ? (
                      <>
                        <Button 
                          type="primary" 
                          icon={<PlusOutlined />}
                          onClick={() => {
                            setViewingRecord(null);
                            setIsCityMode(false);
                            setIsModalVisible(true);
                          }}
                        >
                          新增省级政策情况
                        </Button>
                        <Button 
                          type="primary" 
                          icon={<PlusOutlined />}
                          onClick={() => {
                            setViewingRecord(null);
                            setIsCityMode(true);
                            setIsModalVisible(true);
                          }}
                        >
                          新增主责城市政策情况
                        </Button>
                      </>
                    ) : isOnlyCityEditor ? (
                      <Button 
                        type="primary" 
                        icon={<PlusOutlined />}
                        onClick={() => {
                          setViewingRecord(null);
                          setIsCityMode(true);
                          setIsModalVisible(true);
                        }}
                      >
                        新增主责城市政策情况
                      </Button>
                    ) : (
                      <Button 
                        type="primary" 
                        icon={<PlusOutlined />}
                        onClick={() => {
                          setViewingRecord(null);
                          setIsCityMode(false);
                          setIsModalVisible(true);
                        }}
                      >
                        新增省级政策情况
                      </Button>
                    )}
                  </div>
                </div>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', 
                  gap: '16px' 
                }}>
                  {data.filter(r => !r.responsible_city).map(record => (
                    <Card
                      key={record.id}
                      hoverable
                      style={{ 
                        cursor: 'pointer',
                        border: '1px solid #e8e8e8',
                        borderRadius: '8px',
                        borderLeft: (record.approval_status === 'pending') ? '4px solid #faad14' : 
                                   (record.approval_status === 'approved') ? '4px solid #52c41a' :
                                   (record.approval_status === 'rejected') ? '4px solid #ff4d4f' : '4px solid #d9d9d9',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        transition: 'all 0.3s ease',
                        height: '100%'
                      }}
                      bodyStyle={{ padding: '16px', height: '100%' }}
                      onClick={() => handleEdit(record)}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', height: '100%', justifyContent: 'space-between' }}>
                        <div>
                          {/* 标题区域 */}
                          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '12px' }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                                <FileTextOutlined style={{ marginRight: '8px', color: '#1890ff', fontSize: '16px' }} />
                                <span style={{ 
                                  fontWeight: '600', 
                                  color: '#333', 
                                  fontSize: '16px',
                                  lineHeight: '1.4'
                                }}>
                                  {record.ma_name}
                                </span>
                              </div>
                              <div style={{ 
                                fontSize: '14px', 
                                color: '#666',
                                marginLeft: '24px',
                                marginBottom: '8px'
                              }}>
                                {record.rma_name}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', marginLeft: '24px' }}>
                                {getApprovalStatusTag(record.approval_status)}
                              </div>
                            </div>
                          </div>
                          
                          {/* 信息区域 */}
                          <div style={{ 
                            fontSize: '12px', 
                            color: '#999',
                            padding: '8px 12px',
                            backgroundColor: '#fafafa',
                            borderRadius: '4px',
                            border: '1px solid #f0f0f0'
                          }}>
                            <div>分区: <span style={{ color: '#666' }}>{record.division}</span></div>
                            <div style={{ marginTop: '2px' }}>省份: <span style={{ color: '#666' }}>{record.province}</span></div>
                            <div style={{ marginTop: '2px' }}>更新于: <span style={{ color: '#666' }}>{record.updated_at ? new Date(record.updated_at).toLocaleDateString() : '-'}</span></div>
                          </div>
                        </div>
                        
                        {/* 操作按钮区域 */}
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', paddingTop: '4px', minHeight: '32px', alignItems: 'center' }}>
                          <Button 
                            type="primary" 
                            size="small"
                            icon={<EditOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(record);
                            }}
                          >
                            编辑
                          </Button>
                          {((isAdmin || isSuperAdmin) && (record.approval_status === 'pending' || !record.approval_status)) || 
                           (canDelegateVisitorManage(record) && (record.approval_status === 'pending' || !record.approval_status)) ? (
                            <Button 
                              type="default"
                              size="small"
                              icon={<AuditOutlined />}
                              style={{ color: '#faad14', borderColor: '#faad14' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleApproval(record);
                              }}
                            >
                              审批
                            </Button>
                          ) : null}
                          <Popconfirm
                            title="确定要删除这条记录吗？"
                            onConfirm={(e) => {
                              e.stopPropagation();
                              handleDelete(record.id);
                            }}
                            okText="确定"
                            cancelText="取消"
                          >
                            <Button 
                              type="primary" 
                              danger
                              size="small"
                              icon={<DeleteOutlined />}
                              onClick={(e) => e.stopPropagation()}
                            >
                              删除
                            </Button>
                          </Popconfirm>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}
            
            {/* 主责城市政策情况 */}
            {data.filter(r => r.responsible_city).length > 0 && (
              <div>
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '16px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{
                      width: '4px',
                      height: '22px',
                      backgroundColor: '#722ed1',
                      borderRadius: '2px',
                      marginRight: '12px'
                    }} />
                    <span style={{ 
                      fontSize: '18px', 
                      fontWeight: '600', 
                      color: '#722ed1'
                    }}>
                      主责城市政策情况
                    </span>
                    <span style={{
                      marginLeft: '12px',
                      fontSize: '13px',
                      color: '#999'
                    }}>
                      共 {data.filter(r => r.responsible_city).length} 条
                    </span>
                    {/* 完成率统计 */}
                    {isAdmin && user.region && (
                      <Popover
                        content={
                          <div style={{ maxWidth: '300px' }}>
                            {(() => {
                              const stats = calculateCompletionStats(user.region === 'north' ? '北区' : user.region === 'east' ? '东区' : user.region === 'south' ? '南区' : '中区', data);
                              return (
                                <div>
                                  <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>已填写主责城市 ({stats.cityCompletion.filled}):</div>
                                  <div style={{ marginBottom: '12px', color: '#52c41a', maxHeight: '200px', overflow: 'auto' }}>
                                    {stats.cityCompletion.filledList.length > 0 ? stats.cityCompletion.filledList.join('、') : '无'}
                                  </div>
                                  <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>未填写主责城市 ({stats.cityCompletion.unfilledList.length}):</div>
                                  <div style={{ color: '#ff4d4f', maxHeight: '200px', overflow: 'auto' }}>
                                    {stats.cityCompletion.unfilledList.length > 0 ? stats.cityCompletion.unfilledList.join('、') : '无'}
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        }
                        title="主责城市完成情况"
                        trigger="hover"
                      >
                        <span style={{
                          marginLeft: '16px',
                          padding: '2px 8px',
                          backgroundColor: '#f9f0ff',
                          color: '#722ed1',
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}>
                          {(() => {
                            const stats = calculateCompletionStats(user.region === 'north' ? '北区' : user.region === 'east' ? '东区' : user.region === 'south' ? '南区' : '中区', data);
                            return `完成率: ${stats.cityCompletion.filled}/${stats.cityCompletion.total} (${stats.cityCompletion.rate}%)`;
                          })()}
                        </span>
                      </Popover>
                    )}
                  </div>
                  
                  {/* 新增按钮区域 - 仅当没有省级记录时显示 */}
                  {data.filter(r => !r.responsible_city).length === 0 && (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {/* 管理员、同时拥有editor和city_editor能力的用户，显示两个按钮 */}
                      {isAdmin || hasBothCapabilities ? (
                        <>
                          <Button 
                            type="primary" 
                            icon={<PlusOutlined />}
                            onClick={() => {
                              setViewingRecord(null);
                              setIsCityMode(false);
                              setIsModalVisible(true);
                            }}
                          >
                            新增省级政策情况
                          </Button>
                          <Button 
                            type="primary" 
                            icon={<PlusOutlined />}
                            onClick={() => {
                              setViewingRecord(null);
                              setIsCityMode(true);
                              setIsModalVisible(true);
                            }}
                          >
                            新增主责城市政策情况
                          </Button>
                        </>
                      ) : isOnlyCityEditor ? (
                        <Button 
                          type="primary" 
                          icon={<PlusOutlined />}
                          onClick={() => {
                            setViewingRecord(null);
                            setIsCityMode(true);
                            setIsModalVisible(true);
                          }}
                        >
                          新增主责城市政策情况
                        </Button>
                      ) : (
                        <Button 
                          type="primary" 
                          icon={<PlusOutlined />}
                          onClick={() => {
                            setViewingRecord(null);
                            setIsCityMode(false);
                            setIsModalVisible(true);
                          }}
                        >
                          新增省级政策情况
                        </Button>
                      )}
                    </div>
                  )}
                </div>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', 
                  gap: '16px' 
                }}>
                  {data.filter(r => r.responsible_city).map(record => (
                    <Card
                      key={record.id}
                      hoverable
                      style={{ 
                        cursor: 'pointer',
                        border: '1px solid #e8e8e8',
                        borderRadius: '8px',
                        borderLeft: (record.approval_status === 'pending') ? '4px solid #faad14' : 
                                   (record.approval_status === 'approved') ? '4px solid #52c41a' :
                                   (record.approval_status === 'rejected') ? '4px solid #ff4d4f' : '4px solid #d9d9d9',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        transition: 'all 0.3s ease',
                        height: '100%'
                      }}
                      bodyStyle={{ padding: '16px', height: '100%' }}
                      onClick={() => handleEdit(record)}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', height: '100%', justifyContent: 'space-between' }}>
                        <div>
                          {/* 标题区域 */}
                          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '12px' }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                                <FileTextOutlined style={{ marginRight: '8px', color: '#722ed1', fontSize: '16px' }} />
                                <span style={{ 
                                  fontWeight: '600', 
                                  color: '#333', 
                                  fontSize: '16px',
                                  lineHeight: '1.4'
                                }}>
                                  {record.ma_name}
                                </span>
                              </div>
                              <div style={{ 
                                fontSize: '14px', 
                                color: '#666',
                                marginLeft: '24px',
                                marginBottom: '8px'
                              }}>
                                {record.rma_name}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', marginLeft: '24px' }}>
                                {getApprovalStatusTag(record.approval_status)}
                              </div>
                            </div>
                          </div>
                          
                          {/* 信息区域 */}
                          <div style={{ 
                            fontSize: '12px', 
                            color: '#999',
                            padding: '8px 12px',
                            backgroundColor: '#f9f0ff',
                            borderRadius: '4px',
                            border: '1px solid #d3adf7'
                          }}>
                            <div>分区: <span style={{ color: '#666' }}>{record.division}</span></div>
                            <div style={{ marginTop: '2px' }}>省份: <span style={{ color: '#666' }}>{record.province}</span></div>
                            <div style={{ marginTop: '2px' }}>主责城市: <span style={{ color: '#722ed1', fontWeight: 'bold' }}>{record.responsible_city}</span></div>
                            <div style={{ marginTop: '2px' }}>更新于: <span style={{ color: '#666' }}>{record.updated_at ? new Date(record.updated_at).toLocaleDateString() : '-'}</span></div>
                          </div>
                        </div>
                        
                        {/* 操作按钮区域 */}
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', paddingTop: '4px', minHeight: '32px', alignItems: 'center' }}>
                          <Button 
                            type="primary" 
                            size="small"
                            icon={<EditOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(record);
                            }}
                          >
                            编辑
                          </Button>
                          {((isAdmin || isSuperAdmin) && (record.approval_status === 'pending' || !record.approval_status)) || 
                           (canDelegateVisitorManage(record) && (record.approval_status === 'pending' || !record.approval_status)) ? (
                            <Button 
                              type="default"
                              size="small"
                              icon={<AuditOutlined />}
                              style={{ color: '#faad14', borderColor: '#faad14' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleApproval(record);
                              }}
                            >
                              审批
                            </Button>
                          ) : null}
                          <Popconfirm
                            title="确定要删除这条记录吗？"
                            onConfirm={(e) => {
                              e.stopPropagation();
                              handleDelete(record.id);
                            }}
                            okText="确定"
                            cancelText="取消"
                          >
                            <Button 
                              type="primary" 
                              danger
                              size="small"
                              icon={<DeleteOutlined />}
                              onClick={(e) => e.stopPropagation()}
                            >
                              删除
                            </Button>
                          </Popconfirm>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          // 超级管理员和所有访问者视图：标签页系统
          <Tabs
            defaultActiveKey="cards"
            tabBarExtraContent={
              isSuperAdmin ? (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Button 
                    type="primary" 
                    icon={<PlusOutlined />}
                    onClick={() => {
                      setViewingRecord(null);
                      setIsCityMode(false);
                      setIsModalVisible(true);
                    }}
                  >
                    新增省级政策情况
                  </Button>
                  <Button 
                    type="primary" 
                    icon={<PlusOutlined />}
                    onClick={() => {
                      setViewingRecord(null);
                      setIsCityMode(true);
                      setIsModalVisible(true);
                    }}
                  >
                    新增主责城市政策情况
                  </Button>
                </div>
              ) : null
            }
            items={[
              {
                key: 'cards',
                label: (
                  <span>
                    <RightOutlined />
                    卡片视图
                  </span>
                ),
                children: (
                  <div>
                    {/* 分区列表 */}
                    <Collapse
                      defaultActiveKey={getDefaultActiveKeys()}
                      style={{ background: '#fff', borderRadius: '8px' }}
                    >
                      {getSortedDivisionOrder().map(division => {
                        const divisionProvinces = groupedData[division];
                        if (!divisionProvinces) return null;
                        
                        const totalRecords = Object.values(divisionProvinces).flat().length;
                        const provinceCount = Object.keys(divisionProvinces).length;
                        const stats = calculateCompletionStats(division, data);
                        
                        return (
                          <Panel
                            header={
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                                  <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff' }}>
                                    {division}
                                  </span>
                                  <span style={{ fontSize: '14px', color: '#666' }}>
                                    共 {provinceCount} 个省份，{totalRecords} 条记录
                                  </span>
                                  <Popover
                                    content={
                                      <div style={{ maxWidth: '300px' }}>
                                        <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>已填写省份 ({stats.provinceCompletion.filled}):</div>
                                        <div style={{ marginBottom: '12px', color: '#52c41a' }}>
                                          {stats.provinceCompletion.filledList.length > 0 ? stats.provinceCompletion.filledList.join('、') : '无'}
                                        </div>
                                        <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>未填写省份 ({stats.provinceCompletion.unfilledList.length}):</div>
                                        <div style={{ color: '#ff4d4f' }}>
                                          {stats.provinceCompletion.unfilledList.length > 0 ? stats.provinceCompletion.unfilledList.join('、') : '无'}
                                        </div>
                                      </div>
                                    }
                                    title="省级完成情况"
                                    trigger="hover"
                                  >
                                    <span style={{
                                      padding: '2px 8px',
                                      backgroundColor: '#e6f7ff',
                                      color: '#1890ff',
                                      borderRadius: '4px',
                                      fontSize: '12px',
                                      cursor: 'pointer'
                                    }}>
                                      省级: {stats.provinceCompletion.filled}/{stats.provinceCompletion.total} ({stats.provinceCompletion.rate}%)
                                    </span>
                                  </Popover>
                                  <Popover
                                    content={
                                      <div style={{ maxWidth: '300px' }}>
                                        <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>已填写主责城市 ({stats.cityCompletion.filled}):</div>
                                        <div style={{ marginBottom: '12px', color: '#52c41a', maxHeight: '200px', overflow: 'auto' }}>
                                          {stats.cityCompletion.filledList.length > 0 ? stats.cityCompletion.filledList.join('、') : '无'}
                                        </div>
                                        <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>未填写主责城市 ({stats.cityCompletion.unfilledList.length}):</div>
                                        <div style={{ color: '#ff4d4f', maxHeight: '200px', overflow: 'auto' }}>
                                          {stats.cityCompletion.unfilledList.length > 0 ? stats.cityCompletion.unfilledList.join('、') : '无'}
                                        </div>
                                      </div>
                                    }
                                    title="主责城市完成情况"
                                    trigger="hover"
                                  >
                                    <span style={{
                                      padding: '2px 8px',
                                      backgroundColor: '#f9f0ff',
                                      color: '#722ed1',
                                      borderRadius: '4px',
                                      fontSize: '12px',
                                      cursor: 'pointer'
                                    }}>
                                      城市: {stats.cityCompletion.filled}/{stats.cityCompletion.total} ({stats.cityCompletion.rate}%)
                                    </span>
                                  </Popover>
                                </div>
                              </div>
                            }
                            key={division}
                            style={{ marginBottom: '8px' }}
                          >
                            <div style={{ padding: '8px' }}>
                              {/* 直接显示该分区的所有数据，按类型分类 */}
                              {(() => {
                                const allRecords = Object.values(divisionProvinces).flat();
                                const provinceRecords = allRecords.filter(r => !r.responsible_city);
                                const cityRecords = allRecords.filter(r => r.responsible_city);
                                
                                return (
                                  <>
                                    {/* 省级政策情况 */}
                                    {provinceRecords.length > 0 && (
                                      <div style={{ marginBottom: '16px' }}>
                                        <div style={{ 
                                          display: 'flex',
                                          alignItems: 'center',
                                          marginBottom: '12px',
                                        }}>
                                          <div style={{
                                            width: '4px',
                                            height: '22px',
                                            backgroundColor: '#1890ff',
                                            borderRadius: '2px',
                                            marginRight: '12px'
                                          }} />
                                          <span style={{ 
                                            fontSize: '18px', 
                                            fontWeight: '600', 
                                            color: '#1890ff'
                                          }}>
                                            省级政策情况
                                          </span>
                                          <span style={{
                                            marginLeft: '12px',
                                            fontSize: '13px',
                                            color: '#999'
                                          }}>
                                            共 {provinceRecords.length} 条
                                          </span>
                                        </div>
                                        <div style={{ 
                                          display: 'grid', 
                                          gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', 
                                          gap: '16px'
                                        }}>
                                          {provinceRecords.map(record => (
                                            <Card
                                              key={record.id}
                                              hoverable
                                              style={{ 
                                                cursor: 'pointer',
                                                border: '1px solid #e8e8e8',
                                                borderRadius: '8px',
                                                borderLeft: (record.approval_status === 'pending') ? '4px solid #faad14' : 
                                                           (record.approval_status === 'approved') ? '4px solid #52c41a' :
                                                           (record.approval_status === 'rejected') ? '4px solid #ff4d4f' : '4px solid #d9d9d9',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                                                transition: 'all 0.3s ease',
                                                height: '100%'
                                              }}
                                              bodyStyle={{ padding: '16px', height: '100%' }}
                                              onClick={() => {
                                                if (isSuperAdmin || canDelegateVisitorManage(record)) {
                                                  handleEdit(record);
                                                } else {
                                                  handleViewDetail(record);
                                                }
                                              }}
                                            >
                                              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', height: '100%', justifyContent: 'space-between' }}>
                                                <div>
                                                  {/* 标题区域 */}
                                                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '12px' }}>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                                                        <FileTextOutlined style={{ marginRight: '8px', color: '#1890ff', fontSize: '16px' }} />
                                                        <span style={{ 
                                                          fontWeight: '600', 
                                                          color: '#333', 
                                                          fontSize: '16px',
                                                          lineHeight: '1.4'
                                                        }}>
                                                          {record.ma_name}
                                                        </span>
                                                      </div>
                                                      <div style={{ 
                                                        fontSize: '14px', 
                                                        color: '#666',
                                                        marginLeft: '24px',
                                                        marginBottom: '8px'
                                                      }}>
                                                        {record.rma_name}
                                                      </div>
                                                      <div style={{ display: 'flex', alignItems: 'center', marginLeft: '24px' }}>
                                                        {getApprovalStatusTag(record.approval_status)}
                                                      </div>
                                                    </div>
                                                  </div>
                                                  
                                                  {/* 信息区域 */}
                                                  <div style={{ 
                                                    fontSize: '12px', 
                                                    color: '#999',
                                                    padding: '8px 12px',
                                                    backgroundColor: '#fafafa',
                                                    borderRadius: '4px',
                                                    border: '1px solid #f0f0f0'
                                                  }}>
                                                    <div>分区: <span style={{ color: '#666' }}>{record.division}</span></div>
                                                    <div style={{ marginTop: '2px' }}>省份: <span style={{ color: '#666' }}>{record.province}</span></div>
                                                    <div style={{ marginTop: '2px' }}>更新于: <span style={{ color: '#666' }}>{record.updated_at ? new Date(record.updated_at).toLocaleDateString() : '-'}</span></div>
                                                  </div>
                                                </div>
                                                
                                                {/* 操作按钮区域 - 省级 */}
                                                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', paddingTop: '4px', minHeight: '32px', alignItems: 'center' }}>
                                                  {isSuperAdmin ? (
                                                    // 超级管理员：显示所有按钮
                                                    <>
                                                      <Button 
                                                        type="primary" 
                                                        size="small"
                                                        icon={<EditOutlined />}
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          handleEdit(record);
                                                        }}
                                                      >
                                                        编辑
                                                      </Button>
                                                      {(record.approval_status === 'pending' || !record.approval_status) && (
                                                        <Button 
                                                          type="default"
                                                          size="small"
                                                          icon={<AuditOutlined />}
                                                          style={{ color: '#faad14', borderColor: '#faad14' }}
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleApproval(record);
                                                          }}
                                                        >
                                                          审批
                                                        </Button>
                                                      )}
                                                      <Popconfirm
                                                        title="确定要删除这条记录吗？"
                                                        onConfirm={(e) => {
                                                          e.stopPropagation();
                                                          handleDelete(record.id);
                                                        }}
                                                        okText="确定"
                                                        cancelText="取消"
                                                      >
                                                        <Button 
                                                          type="primary" 
                                                          danger
                                                          size="small"
                                                          icon={<DeleteOutlined />}
                                                          onClick={(e) => e.stopPropagation()}
                                                        >
                                                          删除
                                                        </Button>
                                                      </Popconfirm>
                                                    </>
                                                  ) : canDelegateVisitorManage(record) ? (
                                                    // 代理访问者：可以操作李金雪的数据
                                                    <>
                                                      <Button 
                                                        type="primary" 
                                                        size="small"
                                                        icon={<EditOutlined />}
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          handleEdit(record);
                                                        }}
                                                      >
                                                        编辑
                                                      </Button>
                                                      {(record.approval_status === 'pending' || !record.approval_status) && (
                                                        <Button 
                                                          type="default"
                                                          size="small"
                                                          icon={<AuditOutlined />}
                                                          style={{ color: '#faad14', borderColor: '#faad14' }}
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleApproval(record);
                                                          }}
                                                        >
                                                          审批
                                                        </Button>
                                                      )}
                                                      <Popconfirm
                                                        title="确定要删除这条记录吗？"
                                                        onConfirm={(e) => {
                                                          e.stopPropagation();
                                                          handleDelete(record.id);
                                                        }}
                                                        okText="确定"
                                                        cancelText="取消"
                                                      >
                                                        <Button 
                                                          type="primary" 
                                                          danger
                                                          size="small"
                                                          icon={<DeleteOutlined />}
                                                          onClick={(e) => e.stopPropagation()}
                                                        >
                                                          删除
                                                        </Button>
                                                      </Popconfirm>
                                                    </>
                                                  ) : (
                                                    // 普通访问者：只能查看
                                                    <Button 
                                                      type="primary" 
                                                      size="small"
                                                      icon={<EyeOutlined />}
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleViewDetail(record);
                                                      }}
                                                    >
                                                      查看详情
                                                    </Button>
                                                  )}
                                                </div>
                                              </div>
                                            </Card>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* 主责城市政策情况 */}
                                    {cityRecords.length > 0 && (
                                      <div>
                                        <div style={{ 
                                          display: 'flex',
                                          alignItems: 'center',
                                          marginBottom: '12px',
                                        }}>
                                          <div style={{
                                            width: '4px',
                                            height: '22px',
                                            backgroundColor: '#722ed1',
                                            borderRadius: '2px',
                                            marginRight: '12px'
                                          }} />
                                          <span style={{ 
                                            fontSize: '18px', 
                                            fontWeight: '600', 
                                            color: '#722ed1'
                                          }}>
                                            主责城市政策情况
                                          </span>
                                          <span style={{
                                            marginLeft: '12px',
                                            fontSize: '13px',
                                            color: '#999'
                                          }}>
                                            共 {cityRecords.length} 条
                                          </span>
                                        </div>
                                        <div style={{ 
                                          display: 'grid', 
                                          gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', 
                                          gap: '16px'
                                        }}>
                                          {cityRecords.map(record => (
                                            <Card
                                              key={record.id}
                                              hoverable
                                              style={{ 
                                                cursor: 'pointer',
                                                border: '1px solid #e8e8e8',
                                                borderRadius: '8px',
                                                borderLeft: (record.approval_status === 'pending') ? '4px solid #faad14' : 
                                                           (record.approval_status === 'approved') ? '4px solid #52c41a' :
                                                           (record.approval_status === 'rejected') ? '4px solid #ff4d4f' : '4px solid #d9d9d9',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                                                transition: 'all 0.3s ease',
                                                height: '100%'
                                              }}
                                              bodyStyle={{ padding: '16px', height: '100%' }}
                                              onClick={() => {
                                                if (isSuperAdmin || canDelegateVisitorManage(record)) {
                                                  handleEdit(record);
                                                } else {
                                                  handleViewDetail(record);
                                                }
                                              }}
                                            >
                                              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', height: '100%', justifyContent: 'space-between' }}>
                                                <div>
                                                  {/* 标题区域 */}
                                                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '12px' }}>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                                                        <FileTextOutlined style={{ marginRight: '8px', color: '#722ed1', fontSize: '16px' }} />
                                                        <span style={{ 
                                                          fontWeight: '600', 
                                                          color: '#333', 
                                                          fontSize: '16px',
                                                          lineHeight: '1.4'
                                                        }}>
                                                          {record.ma_name}
                                                        </span>
                                                      </div>
                                                      <div style={{ 
                                                        fontSize: '14px', 
                                                        color: '#666',
                                                        marginLeft: '24px',
                                                        marginBottom: '8px'
                                                      }}>
                                                        {record.rma_name}
                                                      </div>
                                                      <div style={{ display: 'flex', alignItems: 'center', marginLeft: '24px' }}>
                                                        {getApprovalStatusTag(record.approval_status)}
                                                      </div>
                                                    </div>
                                                  </div>
                                                  
                                                  {/* 信息区域 */}
                                                  <div style={{ 
                                                    fontSize: '12px', 
                                                    color: '#999',
                                                    padding: '8px 12px',
                                                    backgroundColor: '#f9f0ff',
                                                    borderRadius: '4px',
                                                    border: '1px solid #d3adf7'
                                                  }}>
                                                    <div>分区: <span style={{ color: '#666' }}>{record.division}</span></div>
                                                    <div style={{ marginTop: '2px' }}>省份: <span style={{ color: '#666' }}>{record.province}</span></div>
                                                    <div style={{ marginTop: '2px' }}>主责城市: <span style={{ color: '#722ed1', fontWeight: 'bold' }}>{record.responsible_city}</span></div>
                                                    <div style={{ marginTop: '2px' }}>更新于: <span style={{ color: '#666' }}>{record.updated_at ? new Date(record.updated_at).toLocaleDateString() : '-'}</span></div>
                                                  </div>
                                                </div>
                                                
                                                {/* 操作按钮区域 - 城市级 */}
                                                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', paddingTop: '4px', minHeight: '32px', alignItems: 'center' }}>
                                                  {isSuperAdmin ? (
                                                    // 超级管理员：显示所有按钮
                                                    <>
                                                      <Button 
                                                        type="primary" 
                                                        size="small"
                                                        icon={<EditOutlined />}
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          handleEdit(record);
                                                        }}
                                                      >
                                                        编辑
                                                      </Button>
                                                      {(record.approval_status === 'pending' || !record.approval_status) && (
                                                        <Button 
                                                          type="default"
                                                          size="small"
                                                          icon={<AuditOutlined />}
                                                          style={{ color: '#faad14', borderColor: '#faad14' }}
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleApproval(record);
                                                          }}
                                                        >
                                                          审批
                                                        </Button>
                                                      )}
                                                      <Popconfirm
                                                        title="确定要删除这条记录吗？"
                                                        onConfirm={(e) => {
                                                          e.stopPropagation();
                                                          handleDelete(record.id);
                                                        }}
                                                        okText="确定"
                                                        cancelText="取消"
                                                      >
                                                        <Button 
                                                          type="primary" 
                                                          danger
                                                          size="small"
                                                          icon={<DeleteOutlined />}
                                                          onClick={(e) => e.stopPropagation()}
                                                        >
                                                          删除
                                                        </Button>
                                                      </Popconfirm>
                                                    </>
                                                  ) : canDelegateVisitorManage(record) ? (
                                                    // 代理访问者：可以操作李金雪的数据
                                                    <>
                                                      <Button 
                                                        type="primary" 
                                                        size="small"
                                                        icon={<EditOutlined />}
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          handleEdit(record);
                                                        }}
                                                      >
                                                        编辑
                                                      </Button>
                                                      {(record.approval_status === 'pending' || !record.approval_status) && (
                                                        <Button 
                                                          type="default"
                                                          size="small"
                                                          icon={<AuditOutlined />}
                                                          style={{ color: '#faad14', borderColor: '#faad14' }}
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleApproval(record);
                                                          }}
                                                        >
                                                          审批
                                                        </Button>
                                                      )}
                                                      <Popconfirm
                                                        title="确定要删除这条记录吗？"
                                                        onConfirm={(e) => {
                                                          e.stopPropagation();
                                                          handleDelete(record.id);
                                                        }}
                                                        okText="确定"
                                                        cancelText="取消"
                                                      >
                                                        <Button 
                                                          type="primary" 
                                                          danger
                                                          size="small"
                                                          icon={<DeleteOutlined />}
                                                          onClick={(e) => e.stopPropagation()}
                                                        >
                                                          删除
                                                        </Button>
                                                      </Popconfirm>
                                                    </>
                                                  ) : (
                                                    // 普通访问者：只能查看
                                                    <Button 
                                                      type="primary" 
                                                      size="small"
                                                      icon={<EyeOutlined />}
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleViewDetail(record);
                                                      }}
                                                    >
                                                      查看详情
                                                    </Button>
                                                  )}
                                                </div>
                                              </div>
                                            </Card>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          </Panel>
                        );
                      })}
                    </Collapse>
                  </div>
                )
              },
              {
                key: 'table',
                label: (
                  <span>
                    <TableOutlined />
                    数据表格
                  </span>
                ),
                children: <DataTable />
              }
            ]}
          />
        )}
      </Content>

      {/* 详情/编辑 Modal */}
      <MAPolicyForm
        visible={isModalVisible}
        editingRecord={viewingRecord}
        onCancel={handleModalClose}
        onSuccess={handleFormSuccess}
        readOnlyMode={isReadOnly}
        isCityMode={isCityMode}
      />

      {/* 审批 Modal */}
      <ApprovalForm
        visible={isApprovalModalVisible}
        record={approvalRecord}
        action={approvalAction}
        oldData={oldRecordData}
        onCancel={() => {
          setIsApprovalModalVisible(false);
          setApprovalRecord(null);
        }}
        onSuccess={handleApprovalSuccess}
      />
    </Layout>
  );
};

// Main App
function App() {
  return (
    <ConfigProvider locale={zhCN}>
      <AuthProvider>
        <Router>
          <div
            className="App"
            style={{
              minHeight: '100vh',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: '#f5f5f5'
            }}
          >
            <div style={{ flex: 1 }}>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/mars" element={<Dashboard />} />
                <Route path="/" element={<Navigate to="/login" replace />} />
              </Routes>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '12px 16px',
                backgroundColor: '#fff',
                borderTop: '1px solid #e8e8e8',
                fontSize: '14px',
                color: '#595959'
              }}
            >
              <img
                src="/备案图标.png"
                alt="备案图标"
                style={{ width: '18px', height: '18px', objectFit: 'contain' }}
              />
              <a
                href="https://beian.mps.gov.cn/#/query/webSearch?code=11010802047983"
                rel="noreferrer"
                target="_blank"
                style={{ color: '#262626', textDecoration: 'none' }}
              >
                京公网安备11010802047983号
              </a>
            </div>
          </div>
        </Router>
      </AuthProvider>
    </ConfigProvider>
  );
}

export default App;
